import { z } from 'zod';
import { appError, err, ok, } from '@orbit-mind/shared-types';
const BLOCK = z.object({
    scope: z.enum(['map', 'author']),
    target: z.string(),
    title: z.string(),
    createdAt: z.number(),
});
const REPORT = z.object({
    id: z.string(),
    map_id: z.string(),
    author_id: z.string(),
    token: z.string(),
    revision: z.number(),
    title: z.string(),
    reason: z.string(),
    details: z.string(),
    created_at: z.number(),
    status: z.string(),
    reviewed_at: z.number().nullable(),
    review_note: z.string().nullable(),
    active_suspension: z.number(),
});
export function createModerationClient(options) {
    async function call(path, schema, body) {
        try {
            const headers = {
                accept: 'application/json',
                'x-orbit-reader': await options.readerId(),
                ...(body === undefined ? {} : { 'content-type': 'application/json' }),
            };
            const response = await options.fetch(`${options.origin ?? ''}${path}`, {
                method: body === undefined ? 'GET' : 'POST',
                headers,
                credentials: 'same-origin',
                signal: AbortSignal.timeout(15000),
                ...(body === undefined ? {} : { body: JSON.stringify(body) }),
            });
            const value = await response.json().catch(() => null);
            if (!response.ok) {
                const message = typeof value === 'object' &&
                    value !== null &&
                    'message' in value &&
                    typeof value.message === 'string'
                    ? value.message
                    : 'The request could not be completed.';
                return err(appError(response.status === 401
                    ? 'UNAUTHENTICATED'
                    : response.status === 403
                        ? 'FORBIDDEN'
                        : response.status === 429
                            ? 'RATE_LIMITED'
                            : response.status === 404
                                ? 'MAP_NOT_FOUND'
                                : 'VALIDATION_FAILED', message, { retryable: response.status >= 500 }));
            }
            const parsed = schema.safeParse(value);
            return parsed.success
                ? ok(parsed.data)
                : err(appError('VALIDATION_FAILED', 'Invalid moderation response.'));
        }
        catch (cause) {
            return err(appError('NETWORK_UNAVAILABLE', 'Could not reach the service. Please try again.', {
                retryable: true,
                cause,
            }));
        }
    }
    return {
        report: (token, reason, details) => call(`/share/${encodeURIComponent(token)}/report`, z.object({ reportId: z.string() }), {
            reason,
            details,
        }),
        block: (token, scope) => call(`/share/${encodeURIComponent(token)}/block`, z.object({ blocked: z.literal(true) }), {
            scope,
        }),
        listBlocks: () => call('/sync/share-blocks', z.object({ blocks: z.array(BLOCK).max(200) })),
        unblock: (scope, target) => call('/sync/share-blocks', z.object({ removed: z.literal(true) }), { scope, target }),
        reports: (status = 'open', reason = '', cursor) => call(`/sync/moderation/reports?status=${status}&reason=${reason}${cursor === undefined ? '' : `&after=${cursor.createdAt}&afterId=${encodeURIComponent(cursor.id)}`}`, z.object({
            reports: z.array(REPORT).max(50),
            nextCursor: z.object({ createdAt: z.number(), id: z.string() }).nullable(),
        })),
        decide: (id, decision, note) => call(`/sync/moderation/reports/${encodeURIComponent(id)}`, z.object({ reviewed: z.literal(true) }), { decision, note }),
        preview: (id, afterAttachment) => call(`/sync/moderation/reports/${encodeURIComponent(id)}/map${afterAttachment === undefined ? '' : `?afterAttachment=${encodeURIComponent(afterAttachment)}`}`, z
            .object({
            document: z.unknown(),
            revision: z.number(),
            reportedRevision: z.number(),
            title: z.string(),
            attachments: z
                .array(z.object({ id: z.string(), filename: z.string(), size: z.number() }))
                .max(100),
            moreAttachments: z.boolean(),
        })
            .passthrough()),
    };
}
//# sourceMappingURL=moderation.js.map